import { sizeFromEtp } from 'constants/clientSizes';
import { addDays, isoDay } from 'helper/timeLabels';

/**
 * The CRM side of the companies.
 *
 * The list (POST admin/crm/companies) comes from the api: `companiesQuery`
 * is what the page sends, `companiesPage` what it reads of the answer.
 *
 * The record pages, the goals and the statistics still run on the demo, which
 * draws a company's CRM fields from its id (`crmFieldsOf`), so a company keeps
 * the same ones from one load to the next.
 */

export const COMPANIES_PER_PAGE = 20;
export const NO_CRM_FILTERS = { action: 'all', commercial: 'all' };

// The filters of the page, as admin/crm/companies reads them: the api
// answers 400 INVALID_FILTERS to a value it does not know, so an empty one is
// sent as the api's « none » ('' or false) and the minimum only with its mode.
// The search is cut to the api's 100 characters and a minimum it would refuse
// (negative, decimal) is left out, so a typo never turns the list into a load
// error.
const MAX_SEARCH_LENGTH = 100;
const MAX_MISSION_COUNT_MIN = 999999;

const missionCountMinOf = ({ missionCount, missionCountMin }) => {
  if (missionCount !== 'at_least' || missionCountMin === '' || missionCountMin == null) return null;
  const min = Number(missionCountMin);
  return Number.isInteger(min) && min >= 0 && min <= MAX_MISSION_COUNT_MIN ? min : null;
};

export const companiesQuery = ({ tab = 'prospects', search = '', filters = {}, page = 1 }) => {
  const missionCountMin = missionCountMinOf(filters);
  return {
    tab,
    search: search.trim().slice(0, MAX_SEARCH_LENGTH),
    page,
    filters: {
      departments: filters.departments ?? [],
      filterCategory: filters.category ? String(filters.category) : '',
      supplierSearch: filters.suppliers ?? [],
      missionCountMode: filters.missionCount || '',
      ...(missionCountMin !== null ? { missionCountMin } : {}),
      incompleteRemuneration: Boolean(filters.incompleteRemuneration),
      // The sizes of the client (constants/clientSizes); none, every size.
      sizes: filters.sizes ?? [],
      action: filters.action ?? NO_CRM_FILTERS.action,
      commercial: filters.commercial ?? NO_CRM_FILTERS.commercial,
    },
  };
};

// A row of the api for the screen: `lostAt` is an instant there, a day
// (« 2026-09-02 ») for describeCompany.
const companyRow = (row) => ({ ...row, lostAt: row.lostAt ? isoDay(new Date(row.lostAt)) : null });

/**
 * What the page reads of { data, page, totalPages, counts }: the companies of
 * the page, the count of every tab and of the one shown, the page (from 1)
 * out of how many.
 */
export const companiesPage = (answer, tab) => {
  const counts = { prospects: 0, new: 0, old: 0, lost: 0, ...answer?.counts };
  return {
    companies: (answer?.data ?? []).map(companyRow),
    counts,
    count: counts[tab] ?? 0,
    page: answer?.page ?? 1,
    pages: Math.max(1, answer?.totalPages ?? 1),
    perPage: COMPANIES_PER_PAGE,
  };
};

const LOST_REASONS = [
  'Prix trop élevé',
  'A choisi une autre agence',
  'Pas de besoin avant la saison',
  'Recrute en direct',
  'Injoignable depuis 3 mois',
  'Établissement fermé',
];
const PROSPECT_TASKS = ['Envoyer la plaquette tarifaire', 'Relancer la proposition', 'Envoyer le devis'];
const CLIENT_TASKS = ['Mettre à jour la fiche client', 'Préparer le bilan trimestriel', 'Envoyer le contrat cadre'];

// FNV-1a: the same number for the same text, at every load.
export const hashOf = (text) => {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};
// A number below `size` for this record; each salt draws its own.
export const draw = (record, salt, size) => hashOf(`${salt}:${record._id}`) % size;
// The later of two days written « 2026-10-21 »: nothing happens to a company
// before it joined.
const laterDay = (day, other) => (day > other ? day : other);

// Its next callback, appointment or task, within the coming week.
const nextActionOf = (company, client, now) => {
  if (draw(company, 'action', 100) >= 45) return null;
  const at = addDays(now, draw(company, 'action-day', 6));
  at.setHours(8 + draw(company, 'action-hour', 9), draw(company, 'action-minutes', 2) * 30, 0, 0);
  const kind = draw(company, 'action-kind', 3);
  if (kind === 0) return { kind: 'callback', at: at.toISOString() };
  if (kind === 1) {
    const category = client ? ['visite_client', 'rdv_client'][draw(company, 'action-category', 2)] : 'prospection';
    return { kind: 'appointment', category, at: at.toISOString() };
  }
  const titles = client ? CLIENT_TASKS : PROSPECT_TASKS;
  return { kind: 'task', title: titles[draw(company, 'action-title', titles.length)], dueOn: isoDay(at) };
};

/**
 * The CRM fields of a real company. `meId` is the signed-in user, who gets a
 * third of the companies; `team` the people a company can be assigned to.
 */
export const crmFieldsOf = (company, { meId, team, now }) => {
  const client = company.missionCount > 0;
  const lost = draw(company, 'lost', client ? 10 : 6) === 0;
  const signedLately = client && draw(company, 'new', 12) === 0;
  const joined = company.createdAt ? isoDay(new Date(company.createdAt)) : isoDay(now);
  const others = team.filter((person) => person.id !== meId);
  const commercialId =
    draw(company, 'commercial', 3) === 0 || !others.length
      ? meId
      : others[draw(company, 'colleague', others.length)].id;
  const recruiterId =
    client && team.length && draw(company, 'recruiter', 5) >= 2 ? team[draw(company, 'recruiter-who', team.length)].id : null;
  const relaunches = lost && draw(company, 'relaunch', 5) < 3;
  return {
    stage: lost ? 'lost' : client ? 'client' : 'prospect',
    clientSince: client
      ? signedLately
        ? laterDay(isoDay(addDays(now, -(3 + draw(company, 'since', 80)))), joined)
        : joined
      : null,
    lostAt: lost ? laterDay(isoDay(addDays(now, -(5 + draw(company, 'lost-at', 150)))), joined) : null,
    lostReason: lost ? LOST_REASONS[draw(company, 'reason', LOST_REASONS.length)] : null,
    relaunchOn: relaunches
      ? isoDay(new Date(now.getFullYear(), now.getMonth() + 1 + draw(company, 'relaunch-month', 6), 1))
      : null,
    commercialId,
    recruiterId,
    nextAction: lost ? null : nextActionOf(company, client, now),
    // Grand compte, PME or TPE (constants/clientSizes): a prospect's is chosen
    // (unknown until then), a client's follows its ETP (crmRecordApi `sizedCrm`).
    size: null,
    // A client's full-time temp workers: the hours of its timesheets over the
    // last 3 whole months, divided by 151,67 h a month and by 3.
    etp: client ? Math.round(2 + draw(company, 'etp', 90)) / 10 : null,
  };
};

// The ETP from which a client is a key account, then an SME (« Tableau
// pilotage »: 5 and 3), as the admin console sets them.
const DEFAULT_SIZE_THRESHOLDS = { grand_compte: 5, pme: 3 };
export const thresholdsOf = (db) => ({ ...DEFAULT_SIZE_THRESHOLDS, ...db.clientSizeSettings });
// A client with timesheets takes its size from its ETP; any other company
// keeps the one chosen (`sizeSource` says which).
export const sizedCrm = (crm, db) =>
  crm.stage === 'client' && crm.etp != null
    ? { ...crm, size: sizeFromEtp(crm.etp, thresholdsOf(db)), sizeSource: 'etp' }
    : { ...crm, sizeSource: 'chosen' };

// The real companies with their CRM fields; `overrides` holds what was
// changed since (an assignment), by company id.
export const withCrmFields = (companies, { meId, team, now, overrides = {} }) =>
  companies.map((company) => ({
    ...company,
    ...crmFieldsOf(company, { meId, team, now }),
    ...overrides[company._id],
  }));
